import { BellOutlined } from '@ant-design/icons';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Badge, Dropdown, List, Tag } from 'antd';
import { useState } from 'react';

import { axiosClient } from '@/api/axiosClient';

interface MyNotificationRow {
  body: string;
  category: string;
  created_at: string;
  id: string; // recipient id
  is_read: boolean;
  title: string;
}

// E1 — chuông thông báo in-app cho tài khoản CMS (QLĐ/HCNS/Owner...).
// BE: /notification/my + /unread-count đọc theo claims.ID (permission
// TB_WORKER_XEM — Owner/Admin bypass; role Member cần được gán code).
// Lỗi (vd 403 do role chưa gán) → ẩn chuông, không phá header.
const NotificationBell = () => {
  const [open, setOpen] = useState(false);

  const {
    data: unreadRes,
    isError,
    refetch: refetchUnread,
  } = useQuery(['notification-unread'], () => axiosClient.get('/notification/unread-count'), {
    refetchInterval: 60000,
    refetchOnWindowFocus: false,
    retry: false,
  });
  const unread = Number(
    (unreadRes as unknown as { data?: { unread?: number } })?.data?.unread ?? 0
  );

  const { data: listRes, refetch: refetchList } = useQuery(
    ['notification-my'],
    () => axiosClient.get('/notification/my?page=1&limit=10'),
    { enabled: open, refetchOnWindowFocus: false, retry: false }
  );
  const rows = (listRes as unknown as { data?: { rows?: MyNotificationRow[] } })?.data?.rows ?? [];

  const markRead = useMutation(
    (recipientId: string) => axiosClient.patch(`/notification/${recipientId}/read`),
    {
      onSuccess: () => {
        refetchList();
        refetchUnread();
      },
    }
  );

  if (isError) return null;

  const dropdown = (
    <div
      className="bg-white shadow rounded"
      style={{ maxHeight: 420, overflowY: 'auto', width: 360 }}
    >
      <List
        dataSource={rows}
        header={<strong className="px-3">Thông báo của tôi</strong>}
        locale={{ emptyText: 'Chưa có thông báo nào' }}
        renderItem={(item) => (
          <List.Item
            className="px-3"
            onClick={() => {
              if (!item.is_read) markRead.mutate(item.id);
            }}
            style={{ background: item.is_read ? undefined : '#e6f4ff', cursor: 'pointer' }}
          >
            <List.Item.Meta
              description={
                <>
                  <div>{item.body}</div>
                  <small className="text-muted">
                    {(item.created_at || '').slice(0, 16).replace('T', ' ')}
                  </small>
                </>
              }
              title={
                <>
                  {item.title} {!item.is_read && <Tag color="blue">Mới</Tag>}
                </>
              }
            />
          </List.Item>
        )}
        size="small"
      />
    </div>
  );

  return (
    <Dropdown
      dropdownRender={() => dropdown}
      onOpenChange={setOpen}
      open={open}
      placement="bottomRight"
      trigger={['click']}
    >
      <span className="cursor-pointer d-flex align-items-center px-2">
        <Badge count={unread} size="small">
          <BellOutlined rev="" style={{ fontSize: 20 }} />
        </Badge>
      </span>
    </Dropdown>
  );
};

export { NotificationBell };
